<template>
  <div v-if="chartData" class="relative h-full w-full flex-1">
    <DoughnutChart
      v-bind="doughnutChartProps"
      :height="height"
      class="w-full h-full absolute top-0 left-0 right-0 bottom-0 mx-auto my-auto"
    >
    </DoughnutChart>
  </div>
  <div v-else class=""></div>
</template>

<script setup lang="ts">
import type { Chart } from 'chart.js';
import { registerables } from 'chart.js'; // TODO: Optimize
import 'chartjs-adapter-moment';
import { DoughnutChart, useDoughnutChart } from 'vue-chart-3';

const ChartJS = useChartJs();

ChartJS.register(...registerables); // TODO: Optimize

type ChartData = Chart<'doughnut'>['data'];

type LabelContext = {
  label: string;
  parsed: number | null;
};

const props = defineProps({
  data: {
    type: Array as PropType<
      { text: string; label: string; value: number; color: string }[]
    >,
    default: () => []
  },
  total: {
    type: Number
  },
  tooltipLabelCallback: {
    type: Function as PropType<(context: LabelContext) => string | string[]>
  },
  centerText: {
    type: String
  },
  height: {
    type: Number,
    default: null
  }
});

const chartData = computed<ChartData | null>(() => {
  return {
    labels: props.data.map(item => item.label || item.text),
    datasets: [
      {
        label: props.data.map(item => item.text),
        borderColor: 'white',
        backgroundColor: props.data.map(item => item.color),
        showLine: true,
        data: props.data.map(item => item.value)
      }
    ]
  };
});

const options = computed(() => {
  return {
    borderWidth: 2,
    elements: {
      center: {
        text: props.centerText || ''
      }
    },
    plugins: {
      crosshair: {
        enabled: false
      },
      empty: {
        label: ' '
      },
      legend: {
        position: () => (window.innerWidth < 420 ? 'top' : 'left'),
        align: () => (window.innerWidth < 420 ? 'start' : 'center'),
        labels: {
          boxWidth: 16,
          boxHeight: 16,
          padding: 8,
          font: {
            size: 12,
            wordWrap: false
          }
        }
      },
      tooltip: {
        callbacks: {
          title: () => '',
          label: function (context) {
            if (props.tooltipLabelCallback) {
              return props.tooltipLabelCallback(context);
            } else {
              const label =
                context.dataset.label?.[context.dataIndex] || context.label;
              return [label, context.parsed].filter(Boolean).join(': ');
            }
          }
        }
      }
    }
  };
});

const { doughnutChartProps } = useDoughnutChart({
  chartData,
  options
});
</script>
